import * as React from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { F0Alert, F0Dialog, F0Select } from '@factorialco/f0-react'
import { Features } from 'generated/featureDefinitions'
import { ApplicationsDataTableDocument } from 'generated/resources/datatable.graphql'
import {
  ApplicationRowFragment,
  AtsHiringRolesEnum,
  BulkMoveToPhaseDocument,
  BulkMoveToPhaseMutation,
  GetApplicationPhasesDocument,
  AtsMoveToPhaseFeaturesDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useFeatureAvailability } from 'modules/core/hooks/useFeatureAvailability'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useCandidateEmailActions } from 'modules/ats/hooks/candidateEmailActions/useCandidateEmailActions'
import { ApplicationPhase, useApplicationPhases } from 'modules/ats/hooks/useApplicationPhases'
import { sortApplicationPhases } from 'modules/ats/lib/utils/applicationPhase'

import { PhaseChangedSmartActionPreview } from './PhaseChangedSmartActionPreview'

interface Props {
  jobId: string
  applications: Pick<ApplicationRowFragment, 'id'>[] &
    {
      applicationPhase: Pick<ApplicationPhase, 'id'>
    }[]
  onClose: () => void
  onSuccess: () => void
  hiringRole: AtsHiringRolesEnum
}

export const MoveToPhaseModal = ({
  applications,
  jobId,
  onSuccess,
  onClose,
  hiringRole,
}: Props) => {
  const [selectedPhaseId, setSelectedPhaseId] = React.useState<string>()
  const [skipSmartAction, setSkipSmartAction] = React.useState<boolean>(false)
  const { loading, applicationPhases } = useApplicationPhases({
    variables: { atsJobPostingId: jobId },
  })
  const [bulkMoveToPhase, { loading: moving }] = useMutation(BulkMoveToPhaseDocument, {
    extractorFn: (data: BulkMoveToPhaseMutation) => data.atsMutations.bulkMoveToPhaseAtsApplication,
  })
  const { candidateEmailActions } = useCandidateEmailActions(
    {
      atsJobPostingId: jobId,
      enabled: true,
      actionType: 'phase_changed',
      atsApplicationPhaseIds: selectedPhaseId ? [selectedPhaseId] : [],
    },
    { skip: !selectedPhaseId }
  )
  const { featureAvailability } = useFeatureAvailability(Features.ats_smart_actions)
  const orderedApplicationPhases = sortApplicationPhases(applicationPhases)
  const currentApplicationPhaseId = applications[0]?.applicationPhase.id
  const { access, company } = useCurrentSession()
  const { data: featuresData } = useQuery(AtsMoveToPhaseFeaturesDocument)
  const isHiringPlanEnabled =
    featuresData?.ats._policies.sidebarSeeRecruitmentHiringPlanSubLevelItem.can ?? false
  const smartActionsActive = featureAvailability.available
  const smartActionTriggered = candidateEmailActions.length > 0 && !skipSmartAction

  const phaseOptions = orderedApplicationPhases.map((phase) => ({
    label: phase.name,
    value: phase.id,
    disabled: phase.id === currentApplicationPhaseId,
  }))
  const onSubmit = async () => {
    const selectedPhase = applicationPhases.find((phase) => phase.id === selectedPhaseId)

    if (!selectedPhase) return

    const response = await bulkMoveToPhase({
      variables: {
        ids: applications.map((application) => application.id),
        authorId: access.id,
        atsApplicationPhaseId: selectedPhase.id,
        skipSmartAction: skipSmartAction,
      },
      refetchQueries: [ApplicationsDataTableDocument, GetApplicationPhasesDocument],
    })

    const result = handleResult(response, () => undefined)

    if (result.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('ats.candidates.applications.move_to_phase.modal.success_message'),
      })

      if (smartActionsActive && smartActionTriggered) {
        track(trackingEvents.ats.smartActionPerform, {
          company_id: company.id,
          stage_id: [selectedPhase.hiringStage?.id],
          stage_name: [selectedPhase.hiringStage?.name || 'Unassigned'],
          job_position: jobId,
          action_type: 'phase_changed',
        })
      }
      track(trackingEvents.ats.candidateMovedPhase, {
        hiring_role: hiringRole,
        new_phase_id: selectedPhase.id,
        old_phase_id: currentApplicationPhaseId,
        user_id: access.id,
      })

      onSuccess()
    }
  }

  return (
    <F0Dialog
      isOpen
      title={i18n.tp('ats.candidates.applications.move_to_phase.modal.title', {
        count: applications.length,
      })}
      onClose={onClose}
      primaryAction={{
        label: i18n.t('ats.candidates.applications.move_to_phase.modal.submit'),
        onClick: onSubmit,
        disabled: !selectedPhaseId || moving,
      }}
      secondaryAction={{
        label: i18n.t('common.cancel'),
        onClick: onClose,
      }}
    >
      <Box gap="s16">
        {isHiringPlanEnabled && (
          <F0Alert
            variant="warning"
            title={i18n.t('ats.candidates.applications.move_to_phase.modal.vacancy_warning_title')}
            description={i18n.t(
              'ats.candidates.applications.move_to_phase.modal.vacancy_warning_description'
            )}
          />
        )}
        <p>{i18n.t('ats.candidates.applications.move_to_phase.modal.description')}</p>
        <F0Select
          label={i18n.t('ats.candidates.applications.move_to_phase.modal.phase')}
          name="phase"
          placeholder={i18n.t('ats.candidates.applications.move_to_phase.modal.select_prompt')}
          size="md"
          value={selectedPhaseId}
          onChange={(value: string) => setSelectedPhaseId(value)}
          options={phaseOptions}
          readonly={loading}
        />

        {smartActionsActive && candidateEmailActions[0]?.id && (
          <PhaseChangedSmartActionPreview
            onToggleAction={setSkipSmartAction}
            smartAction={candidateEmailActions[0]}
          />
        )}
      </Box>
    </F0Dialog>
  )
}
